import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, SA_ORG_KEY } from "@/lib/api";
import { grantAccess } from "@/lib/accessKey";

const AuthContext = createContext(null);

// Shared demo logins — pin the session to the guest's sample org and keep its live fleet running.
const armDemoGuest = async (u) => {
  if (!u?.demo_only) return;
  const orgId = u.org_id === "org_demo_fleet" ? "org_demo_fleet" : "org_demo_sample";
  try {
    localStorage.setItem("demo_mode", "1");
    localStorage.setItem(SA_ORG_KEY, orgId);
    localStorage.removeItem("plant_id");
  } catch { /* private mode */ }
  try {
    await api.post(orgId === "org_demo_fleet" ? "/demo/fleet/enable" : "/demo/enable");
  } catch { /* sample org already live */ }
};

const hexToRgb = (hex) => {
  const h = (hex || "").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  if (full.length !== 6) return null;
  const n = parseInt(full, 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
};

const applyBrand = (org) => {
  const root = document.documentElement;
  const rgb = hexToRgb(org?.brand_primary);
  if (rgb && org?.brand_primary?.toUpperCase() !== "#F57C00") {
    root.style.setProperty("--primary", rgb);
    root.style.setProperty("--primary-hover", rgb);
  } else {
    root.style.removeProperty("--primary");
    root.style.removeProperty("--primary-hover");
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null=checking, false=guest, obj=user
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshOrg = useCallback(async () => {
    try {
      const { data } = await api.get("/org/me");
      setOrg(data);
      applyBrand(data);
      return data;
    } catch (e) {
      return null;
    }
  }, []);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => { setUser(r.data); armDemoGuest(r.data); refreshOrg(); })
      .catch((e) => {
        // A trial org past its window gets 403 TRIAL_EXPIRED on every call — that's a
        // signed-in user who has run out of time, not a guest.
        if (e.response?.status === 403 && e.response?.data?.detail === "TRIAL_EXPIRED") setUser("trial_expired");
        else setUser(false);
      })
      .finally(() => setLoading(false));
  }, [refreshOrg]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    // A signed-in device keeps direct access to the login screen from now on.
    grantAccess();
    await armDemoGuest(data);
    refreshOrg();
    return data;
  };

  const loginWithPin = async (pin) => {
    const { data } = await api.post("/auth/driver-pin-login", { pin });
    setUser(data);
    grantAccess();
    refreshOrg();
    return data;
  };

  const demoLogin = async () => {
    const { data } = await api.post("/auth/demo-login");
    setUser(data);
    await armDemoGuest(data);
    refreshOrg();
    return data;
  };

  const fleetDemoLogin = async () => {
    const { data } = await api.post("/auth/fleet-demo-login");
    setUser(data);
    await armDemoGuest(data);
    refreshOrg();
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {}
    setUser(false);
    setOrg(null);
    applyBrand(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, org, refreshOrg, loading, login, loginWithPin, demoLogin, fleetDemoLogin, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
